import React from 'react';
import { Modal, ModalContent, ModalOverlay, ModalCloseButton } from '../ui/modal';
import { ModalNames, useModalState } from '../../misc/wrappers/modal-provider';
import { SubmitHandler, useForm } from 'react-hook-form';
import { signInViaEmailCodeSchema } from 'shared/validation/schemas';
import { yupResolver } from '@hookform/resolvers/yup';
import PerfInput from '../ui/perf-input';
import { TWButton } from '../button';
import { useMutation } from '@apollo/client';
import { SIGN_IN_VIA_CODE } from 'shared/misc/graphql/SignInViewFragments';
import { useAuth } from 'shared/misc/hooks/useAuth';
import { useToast } from '@chakra-ui/react';
import { useRouter } from '@tanstack/react-router';
import { USER_SIGN_UP_STEP } from 'shared/data/user';
import ThemeAwareStar from 'shared/misc/components/ThemeAwareStar';
import { usePathname } from '../../utils/tan-router';

type InputsSignInWithCode = {
  code: string;
};

interface Props {
  email?: string;
  onSuccess?: () => void;
  destination?: string;
}

export default function VerifyModal({ email, onSuccess, destination }: Props) {
  const { isOpen, onOpen, onClose } = useModalState(ModalNames.VERIFY);

  const { signIn } = useAuth();
  const toast = useToast();
  const router = useRouter();

  const [verifyEmailAndSignInViaCode, { loading }] = useMutation(SIGN_IN_VIA_CODE);

  const signInViaEmailCodeForm = useForm<InputsSignInWithCode>({
    resolver: yupResolver(signInViaEmailCodeSchema),
  });
  const pathname = usePathname();
  const onEmailCodeSubmit: SubmitHandler<InputsSignInWithCode> = async (values, event) => {
    event?.preventDefault();

    await verifyEmailAndSignInViaCode({
      variables: { code: values.code },
      onCompleted: async (data) => {
        const me = await signIn(data.verifyEmailAndSignInViaCode);

        if (pathname === '/suggest') {
          document.getElementById('rec-composer-share')?.click();
        } else {
          toast({
            title: 'Welcome back!',
            status: 'success',
            duration: 3000,
            isClosable: true,
            position: 'top',
          });
        }
        handleClose();

        if (onSuccess) {
          onSuccess();
        }
      },
      onError: (e) => {
        toast({
          title: 'Invalid code. Please try again.',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top',
        });
      },
    });
  };

  const handleClose = () => {
    signInViaEmailCodeForm.reset();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <Modal open={isOpen} onOpenChange={(o) => (o ? onOpen() : handleClose())}>
      <ModalOverlay />
      <ModalContent className="w-full sm:w-[500px]">
        <ModalCloseButton />
        <div className="overflow-y-scroll pb-6 pt-6">
          <form onSubmit={signInViaEmailCodeForm.handleSubmit(onEmailCodeSubmit)}>
            <div className="flex flex-col gap-4 w-full">
              <ThemeAwareStar />
              <p className="typography-heading-md">WELCOME BACK!</p>
              <p className="typography-body-sm text-secondaryText text-brand-lightgrey">
                Enter the code sent to your email below.
              </p>
              <div className="flex flex-col gap-1">
                <PerfInput
                  key="code"
                  placeholder="CODE"
                  className="typography-action-sm  md:typography-heading-sm"
                  {...signInViaEmailCodeForm.register('code')}
                  error={signInViaEmailCodeForm.formState.errors.code}
                  autoFocus
                />
              </div>

              <div className="space-y-3 mx-auto">
                <TWButton
                  type="submit"
                  variant="primary"
                  className="typography-action-sm md:typography-heading-sm"
                  disabled={loading}
                >
                  {loading ? 'Verifying...' : 'Sign In'}
                </TWButton>
              </div>
            </div>
          </form>
        </div>
      </ModalContent>
    </Modal>
  );
}
